import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmCheckoutSession, createCheckoutSession, getPricingCatalog } from '../api/auth';
import { useAuth } from '../context/AuthContext';

const CHECKOUT_RETURN_TO_KEY = 'checkout_return_to';
const CHECKOUT_PRODUCT_KEY = 'checkout_product_key';

const styles = `
.pricing-page{
  --bg:#07111f;
  --card:#ffffff;
  --muted:#64748b;
  --text:#0f172a;
  --line:#e2e8f0;
  --blue1:#2563eb;
  --blue2:#1d4ed8;
  --green1:#10b981;
  --green2:#059669;
  --dark1:#334155;
  --dark2:#1e293b;
  --red1:#ef4444;
  --red2:#dc2626;
  --gold1:#f59e0b;
  --gold2:#d97706;
  --shadow:0 25px 55px rgba(15, 23, 42, 0.18);
  --radius:24px;
  margin:0;
  font-family:"Segoe UI", Tahoma, Arial, sans-serif;
  background:
    radial-gradient(circle at top right, rgba(37,99,235,.18), transparent 22%),
    radial-gradient(circle at bottom left, rgba(16,185,129,.14), transparent 20%),
    linear-gradient(180deg, #06111f 0%, #0c1628 100%);
  color:var(--text);
  min-height:100vh;
}
.pricing-page *{box-sizing:border-box}
.pricing-page .wrap{max-width:1240px;margin:0 auto;padding:42px 18px 56px}
.pricing-page .topbar{
  display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;
  margin:0 auto 18px;max-width:1120px;
}
.pricing-page .home-link{
  display:inline-flex;align-items:center;gap:10px;padding:10px 14px;border-radius:999px;
  background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);color:#fff;text-decoration:none;
  box-shadow:0 16px 36px rgba(15,23,42,.18);font-weight:900;
}
.pricing-page .home-link span{display:block;line-height:1.1}
.pricing-page .home-link small{display:block;color:#bfdbfe;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
.pricing-page .back-btn{
  height:42px;padding:0 16px;border-radius:999px;border:1px solid rgba(255,255,255,.16);
  background:rgba(255,255,255,.08);color:#fff;font-weight:900;cursor:pointer;
}
.pricing-page .sticky-promo-bar{
  position:sticky;top:12px;z-index:20;margin:0 auto 16px;max-width:1120px;
  background:linear-gradient(135deg,#dc2626,#f97316 55%,#f59e0b);
  color:#fff;border-radius:24px;padding:16px 18px;box-shadow:0 24px 50px rgba(220,38,38,.28);
  border:1px solid rgba(255,255,255,.18);display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;
}
.pricing-page .sticky-promo-bar strong{font-size:15px;font-weight:1000;letter-spacing:.02em}
.pricing-page .sticky-promo-bar span{font-size:13px;font-weight:800;color:#fff7ed}
.pricing-page .sticky-timer{
  display:flex;gap:10px;align-items:center;flex-wrap:wrap;
}
.pricing-page .sticky-timer-box{
  min-width:76px;background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.18);
  border-radius:16px;padding:8px 10px;text-align:center;
}
.pricing-page .sticky-timer-box strong{display:block;font-size:22px;line-height:1;font-weight:1000}
.pricing-page .sticky-timer-box span{display:block;font-size:10px;margin-top:4px;letter-spacing:.08em;text-transform:uppercase;color:#ffedd5}
.pricing-page .hero{
  position:relative;overflow:hidden;
  background:linear-gradient(135deg, #0b1929 0%, #10274a 55%, #0a1a33 100%);
  border:1px solid rgba(255,255,255,.08);
  border-radius:32px;
  padding:34px 24px 30px;
  text-align:center;
  box-shadow:0 30px 80px rgba(2, 6, 23, 0.45);
  margin-bottom:24px;
}
.pricing-page .hero:before,.pricing-page .hero:after{content:"";position:absolute;border-radius:999px;filter:blur(12px);opacity:.28}
.pricing-page .hero:before{width:240px;height:240px;background:#2563eb;top:-90px;right:-60px}
.pricing-page .hero:after{width:180px;height:180px;background:#10b981;bottom:-70px;left:-35px}
.pricing-page .pill-row{display:flex;justify-content:center;gap:10px;flex-wrap:wrap;margin-bottom:14px;position:relative;z-index:1}
.pricing-page .pill{
  display:inline-flex;align-items:center;gap:8px;border-radius:999px;padding:9px 16px;
  font-size:13px;font-weight:800;color:#fff;
  background:linear-gradient(135deg, var(--red1), var(--red2));
  box-shadow:0 12px 30px rgba(220, 38, 38, .3);
}
.pricing-page .pill.secondary{background:rgba(255,255,255,.08);color:#dbeafe;border:1px solid rgba(255,255,255,.12);box-shadow:none;font-weight:700}
.pricing-page h1{margin:0;color:#fff;font-size:clamp(28px, 5vw, 46px);line-height:1.15;position:relative;z-index:1}
.pricing-page .hero p{max-width:760px;margin:12px auto 0;color:rgba(255,255,255,.76);font-size:16px;line-height:1.8;position:relative;z-index:1}
.pricing-page .countdown{
  margin:22px auto 0;width:fit-content;position:relative;z-index:1;padding:12px 18px;border-radius:18px;color:#fff;
  background:rgba(239, 68, 68, .12);border:1px solid rgba(239, 68, 68, .28);
  font-weight:800;font-size:14px;animation:pulse 1.8s infinite;
}
.pricing-page .hero-timer{
  margin:18px auto 0;position:relative;z-index:1;max-width:720px;
  background:linear-gradient(135deg, rgba(239,68,68,.16), rgba(249,115,22,.16));
  border:1px solid rgba(251,113,133,.28);
  border-radius:24px;padding:18px 20px 16px;
  box-shadow:0 22px 50px rgba(239,68,68,.18);
}
.pricing-page .hero-timer-label{
  color:#fecaca;font-size:12px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;
}
.pricing-page .hero-timer-value{
  display:flex;justify-content:center;gap:12px;flex-wrap:wrap;margin-top:12px;
}
.pricing-page .hero-time-box{
  min-width:96px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);
  border-radius:18px;padding:12px 10px;
}
.pricing-page .hero-time-box strong{
  display:block;color:#fff;font-size:28px;line-height:1;font-weight:1000;
}
.pricing-page .hero-time-box span{
  display:block;color:#fecaca;font-size:11px;font-weight:800;margin-top:6px;letter-spacing:.08em;text-transform:uppercase;
}
.pricing-page .hero-timer-note{
  margin-top:12px;color:#fde68a;font-size:13px;font-weight:800;
}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(239,68,68,.30)}70%{box-shadow:0 0 0 18px rgba(239,68,68,0)}100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}}
.pricing-page .pricing-grid{
  display:grid;
  grid-template-columns:repeat(auto-fit, minmax(300px, 360px));
  gap:18px;
  align-items:stretch;
  justify-content:center;
}
.pricing-page .card{
  position:relative;overflow:visible;background:rgba(255,255,255,.98);
  border-radius:var(--radius);padding:22px 20px 20px;
  box-shadow:var(--shadow);border:1px solid rgba(226,232,240,.95);
  transition:transform .22s ease, box-shadow .22s ease;
}
.pricing-page .card:hover{transform:translateY(-6px);box-shadow:0 32px 65px rgba(15,23,42,.22)}
.pricing-page .card.popular{border:2px solid rgba(16,185,129,.7);transform:scale(1.03)}
.pricing-page .pricing-grid.compact .card.popular{transform:none}
.pricing-page .card.promo{
  background:linear-gradient(180deg,#fff7ed 0%,#ffffff 36%);
  border:2px solid rgba(245,158,11,.38);
  box-shadow:0 28px 60px rgba(245,158,11,.18);
}
.pricing-page .promo-ribbon{
  position:absolute;top:18px;right:-40px;transform:rotate(24deg);
  background:linear-gradient(135deg,#ef4444,#f97316);color:#fff;
  padding:8px 46px;font-size:11px;font-weight:1000;letter-spacing:.08em;
  box-shadow:0 16px 30px rgba(239,68,68,.28);
}
.pricing-page .promo-timer{
  margin:-2px 0 14px;
  display:flex;align-items:center;justify-content:center;gap:10px;
  background:linear-gradient(135deg, rgba(245,158,11,.14), rgba(239,68,68,.12));color:#9a3412;border:1px solid rgba(245,158,11,.25);
  border-radius:18px;padding:12px 14px;font-size:13px;font-weight:1000;
  box-shadow:0 16px 30px rgba(245,158,11,.12);
}
.pricing-page .promo-meta{
  display:flex;gap:8px;flex-wrap:wrap;justify-content:center;margin-bottom:14px;
}
.pricing-page .promo-chip{
  border-radius:999px;padding:7px 11px;font-size:11px;font-weight:900;
  background:#fff;color:#9a3412;border:1px solid rgba(251,146,60,.28);
}
.pricing-page .top-badge{
  position:absolute;top:-14px;right:18px;background:linear-gradient(135deg, var(--gold1), var(--gold2));color:#fff;
  padding:8px 14px;border-radius:999px;font-size:12px;font-weight:900;box-shadow:0 14px 35px rgba(217,119,6,.35);
}
.pricing-page .discount-tag{
  position:absolute;top:-18px;left:16px;width:88px;height:88px;border-radius:50%;
  display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;color:#fff;
  background:linear-gradient(135deg, var(--red1), var(--red2));
  box-shadow:0 18px 38px rgba(220,38,38,.35);
  transform:rotate(-12deg);border:4px solid #fff;
}
.pricing-page .discount-tag strong{font-size:24px;line-height:1}
.pricing-page .discount-tag span{font-size:11px;font-weight:900;line-height:1.1}
.pricing-page .icon{
  width:58px;height:58px;border-radius:18px;display:flex;align-items:center;justify-content:center;
  font-size:26px;margin-bottom:14px;color:#fff;box-shadow:inset 0 1px 0 rgba(255,255,255,.28);
}
.pricing-page .gray .icon{background:linear-gradient(135deg, #475569, #334155)}
.pricing-page .blue .icon{background:linear-gradient(135deg, #3b82f6, #1d4ed8)}
.pricing-page .green .icon{background:linear-gradient(135deg, #10b981, #059669)}
.pricing-page .plan{font-size:21px;font-weight:900;color:var(--text);margin:0}
.pricing-page .sub{font-size:12px;color:var(--muted);margin-top:4px;margin-bottom:14px}
.pricing-page .hook{
  background:#f8fafc;border:1px solid #e2e8f0;color:#0f172a;border-radius:16px;
  padding:11px 12px;font-size:13px;font-weight:800;margin-bottom:14px;line-height:1.6;
}
.pricing-page .green .hook{background:rgba(16,185,129,.08);border-color:rgba(16,185,129,.22)}
.pricing-page .blue .hook{background:rgba(59,130,246,.07);border-color:rgba(59,130,246,.18)}
.pricing-page .price-box{border-top:1px solid var(--line);border-bottom:1px solid var(--line);padding:16px 0 14px;margin-bottom:14px}
.pricing-page .old-price{display:flex;align-items:center;justify-content:center;gap:8px;color:#94a3b8;font-weight:800;margin-bottom:6px}
.pricing-page .old-price .num{text-decoration:line-through;font-size:26px}
.pricing-page .save-line{
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  background:rgba(239,68,68,.08);color:#dc2626;border:1px solid rgba(239,68,68,.18);
  padding:6px 12px;border-radius:999px;font-size:12px;font-weight:900;margin-bottom:8px;
}
.pricing-page .new-price{display:flex;align-items:baseline;justify-content:center;gap:7px;flex-wrap:wrap}
.pricing-page .new-price .currency,.pricing-page .new-price .term{color:var(--muted);font-size:13px;font-weight:700}
.pricing-page .new-price .num{font-size:46px;font-weight:1000;line-height:1;letter-spacing:-1px}
.pricing-page .gray .new-price .num{color:var(--dark2)}
.pricing-page .blue .new-price .num{color:var(--blue2)}
.pricing-page .green .new-price .num{color:var(--green2)}
.pricing-page ul{list-style:none;padding:0;margin:0 0 16px}
.pricing-page li{display:flex;align-items:flex-start;gap:10px;color:#334155;font-size:14px;line-height:1.8;padding:4px 0}
.pricing-page li .tick{
  flex:0 0 auto;width:22px;height:22px;margin-top:2px;border-radius:50%;
  display:inline-flex;align-items:center;justify-content:center;color:#fff;font-size:12px;font-weight:900;
}
.pricing-page .gray li .tick{background:linear-gradient(135deg, #475569, #334155)}
.pricing-page .blue li .tick{background:linear-gradient(135deg, #3b82f6, #1d4ed8)}
.pricing-page .green li .tick{background:linear-gradient(135deg, #10b981, #059669)}
.pricing-page .cta{
  width:100%;border:none;color:#fff;font-size:15px;font-weight:900;padding:14px 16px;border-radius:16px;cursor:pointer;
  box-shadow:0 18px 30px rgba(15,23,42,.15);display:inline-flex;align-items:center;justify-content:center;gap:10px;
  transition:opacity .18s ease, transform .18s ease, filter .18s ease;
}
.pricing-page .gray .cta{background:linear-gradient(135deg, #475569, #334155)}
.pricing-page .blue .cta{background:linear-gradient(135deg, #3b82f6, #1d4ed8)}
.pricing-page .green .cta{background:linear-gradient(135deg, #10b981, #059669)}
.pricing-page .cta:disabled{cursor:not-allowed;filter:saturate(.9)}
.pricing-page .cta.loading{pointer-events:none}
.pricing-page .spinner{
  width:18px;height:18px;border-radius:50%;
  border:2px solid rgba(255,255,255,.28);border-top-color:#fff;
  animation:pricingSpin .72s linear infinite;flex:0 0 auto;
}
.pricing-page .busy-note{
  margin:0 auto 16px;width:fit-content;padding:10px 14px;border-radius:999px;
  background:rgba(37,99,235,.10);color:#dbeafe;border:1px solid rgba(147,197,253,.28);
  font-size:13px;font-weight:800;display:flex;align-items:center;gap:10px;
}
.pricing-page .checkout-success-overlay{
  position:fixed;
  inset:0;
  z-index:120;
  background:rgba(3,7,18,.56);
  backdrop-filter:blur(10px);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:24px;
}
.pricing-page .checkout-success{
  position:relative;
  width:min(760px, 100%);
  border-radius:30px;
  padding:28px 24px;
  background:
    radial-gradient(circle at top right, rgba(16,185,129,.18), transparent 30%),
    radial-gradient(circle at bottom left, rgba(37,99,235,.14), transparent 28%),
    linear-gradient(135deg, rgba(255,255,255,.99), rgba(240,253,244,.99));
  border:1px solid rgba(16,185,129,.24);
  box-shadow:0 30px 80px rgba(15,23,42,.28);
}
.pricing-page .checkout-success-close{
  position:absolute;
  top:16px;
  left:16px;
  width:40px;
  height:40px;
  border:none;
  border-radius:999px;
  background:rgba(255,255,255,.9);
  color:#0f172a;
  font-size:22px;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 12px 28px rgba(15,23,42,.12);
}
.pricing-page .checkout-success-top{
  display:flex;align-items:flex-start;justify-content:space-between;gap:18px;flex-wrap:wrap;
}
.pricing-page .checkout-success-badge{
  width:64px;height:64px;border-radius:22px;display:grid;place-items:center;
  background:linear-gradient(135deg,#10b981,#059669);color:#fff;font-size:30px;font-weight:1000;
  box-shadow:0 20px 40px rgba(5,150,105,.24);
}
.pricing-page .checkout-success-kicker{
  display:inline-flex;align-items:center;gap:8px;
  padding:8px 12px;border-radius:999px;background:rgba(16,185,129,.12);color:#047857;
  font-size:12px;font-weight:900;letter-spacing:.08em;
}
.pricing-page .checkout-success h2{
  margin:12px 0 8px;font-size:clamp(26px,4vw,38px);line-height:1.15;color:#052e16;
}
.pricing-page .checkout-success p{
  margin:0;max-width:720px;color:#166534;font-size:15px;line-height:1.9;font-weight:700;
}
.pricing-page .checkout-success-grid{
  display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-top:22px;
}
.pricing-page .checkout-success-stat{
  background:rgba(255,255,255,.82);border:1px solid rgba(16,185,129,.16);border-radius:20px;padding:16px 18px;
}
.pricing-page .checkout-success-stat strong{
  display:block;font-size:28px;line-height:1;color:#0f172a;font-weight:1000;
}
.pricing-page .checkout-success-stat span{
  display:block;margin-top:8px;color:#475569;font-size:12px;font-weight:800;
}
.pricing-page .checkout-success-actions{
  display:flex;gap:12px;flex-wrap:wrap;margin-top:22px;
}
.pricing-page .checkout-success-primary,
.pricing-page .checkout-success-secondary{
  height:48px;padding:0 18px;border-radius:16px;font-size:14px;font-weight:900;cursor:pointer;
}
.pricing-page .checkout-success-primary{
  border:none;background:linear-gradient(135deg,#10b981,#059669);color:#fff;
  box-shadow:0 18px 34px rgba(5,150,105,.24);
}
.pricing-page .checkout-success-secondary{
  border:1px solid #cbd5e1;background:#fff;color:#0f172a;
}
@keyframes pricingSpin{to{transform:rotate(360deg)}}
.pricing-page .guarantee{
  display:flex;justify-content:center;flex-wrap:wrap;gap:10px;margin-top:20px;color:#dbeafe;font-size:13px;font-weight:700;text-align:center;
}
.pricing-page .guarantee span{
  background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.08);
  border-radius:999px;padding:10px 14px;
}
@media (max-width:980px){
  .pricing-page .pricing-grid{grid-template-columns:1fr}
  .pricing-page .card.popular{transform:none}
  .pricing-page .discount-tag{width:78px;height:78px}
  .pricing-page .sticky-promo-bar{top:8px}
}
`;

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState('');
  const [localMessage, setLocalMessage] = useState('');
  const [checkoutConfirmation, setCheckoutConfirmation] = useState(null);

  useEffect(() => {
    getPricingCatalog().then(setCatalog).catch(console.error);
  }, []);

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const locked = search.get('locked');
  const checkoutState = search.get('checkout');
  const sessionId = search.get('session_id');
  const storedReturnTo = typeof window !== 'undefined' ? window.sessionStorage.getItem(CHECKOUT_RETURN_TO_KEY) || '' : '';
  const returnTo = search.get('return_to') || storedReturnTo || '/platform';
  const backTarget = returnTo || '/';
  const currentPlan = user?.plan || catalog?.current_plan || 'free';
  const plansByKey = Object.fromEntries((catalog?.plans || []).map((item) => [item.key, item]));
  const promoPlan = plansByKey.promo;
  const basicPlan = plansByKey.basic;
  const aiPlan = plansByKey.ai;
  const locale = 'ar';
  const isProcessing = busy !== '';
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const promoEndsAt = promoPlan?.expires_at ? new Date(promoPlan.expires_at) : null;
  const promoCountdown = useMemo(() => {
    if (!promoEndsAt) return '';
    const diff = promoEndsAt.getTime() - nowTs;
    if (Number.isNaN(diff) || diff <= 0) return 'انتهى العرض التجريبي';
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    return `${hours}h ${minutes}m ${seconds}s`;
  }, [promoEndsAt, nowTs]);
  const promoTimeParts = useMemo(() => {
    if (!promoEndsAt) return null;
    const diff = promoEndsAt.getTime() - nowTs;
    if (Number.isNaN(diff) || diff <= 0) return { days: '00', hours: '00', minutes: '00', seconds: '00' };
    const totalSeconds = Math.floor(diff / 1000);
    return {
      days: String(Math.floor(totalSeconds / 86400)).padStart(2, '0'),
      hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0'),
      minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
      seconds: String(totalSeconds % 60).padStart(2, '0'),
    };
  }, [promoEndsAt, nowTs]);
  const visiblePlans = useMemo(() => (catalog?.plans || []).filter((plan) => plan.active || currentPlan === plan.key), [catalog, currentPlan]);
  const productMeta = useMemo(() => ({
    promo: { label: 'Promo Trial', kind: 'خطة' },
    basic: { label: 'Basic Practice', kind: 'خطة' },
    ai: { label: 'AI Practice', kind: 'خطة' },
    credits_5: { label: '5 AI Credits', kind: 'رصيد' },
    credits_10: { label: '10 AI Credits', kind: 'رصيد' },
    credits_25: { label: '25 AI Credits', kind: 'رصيد' },
  }), []);
  const createSuccessSummary = (productKey, refreshedUser) => {
    const meta = productMeta[productKey] || { label: 'Package', kind: 'طلب' };
    return {
      title: meta.kind === 'رصيد' ? 'تمت إضافة الرصيد بنجاح' : 'تم تأكيد عملية الشراء بنجاح',
      body: meta.kind === 'رصيد'
        ? `تمت إضافة ${meta.label} إلى حسابك، وأصبح الرصيد الجديد جاهزاً للاستخدام فوراً.`
        : `تم تفعيل ${meta.label} على حسابك بنجاح، ويمكنك الآن المتابعة واستخدام المزايا الجديدة بدون أي خطوات إضافية.`,
      productLabel: meta.label,
      credits: refreshedUser?.ai_credits ?? user?.ai_credits ?? 0,
      planLabel: refreshedUser?.plan === 'ai' ? 'AI Practice' : refreshedUser?.plan === 'basic' ? 'Basic Practice' : refreshedUser?.plan === 'promo' ? 'Promo Trial' : 'Free',
    };
  };
  const clearCheckoutQuery = () => {
    const nextSearch = returnTo && returnTo !== '/platform'
      ? `?return_to=${encodeURIComponent(returnTo)}`
      : '';
    navigate(`/pricing${nextSearch}`, { replace: true });
  };
  const continueLabel = returnTo && returnTo !== '/platform' ? 'العودة إلى الصفحة السابقة' : 'الذهاب إلى المنصة';
  const localize = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    return value[locale] || value.en || fallback;
  };
  const featuresFor = (item, fallback = []) => {
    const localized = item?.content?.features?.[locale];
    return Array.isArray(localized) && localized.length ? localized : fallback;
  };
  const closeCheckoutModal = () => {
    setCheckoutConfirmation(null);
    navigate('/landing', { replace: true });
  };

  useEffect(() => {
    if (checkoutState !== 'success' || !sessionId) return;
    const target = `/pricing?checkout=success&session_id=${encodeURIComponent(sessionId)}&return_to=${encodeURIComponent(returnTo)}`;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
    }
  }, [checkoutState, sessionId, user, navigate, returnTo]);

  useEffect(() => {
    if (returnTo && returnTo !== '/platform') {
      window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, returnTo);
    }
  }, [returnTo]);

  const handleCheckout = async (productKey) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/pricing?return_to=${encodeURIComponent(returnTo)}`)}`);
      return;
    }
    try {
      setBusy(productKey);
      window.sessionStorage.setItem(CHECKOUT_PRODUCT_KEY, productKey);
      if (returnTo && returnTo !== '/platform') {
        window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, returnTo);
      }
      const result = await createCheckoutSession(productKey, returnTo, window.location.origin);
      if (result.mode === 'mock') {
        const refreshedUser = await refreshUser();
        setLocalMessage('تم تفعيل الخطة محلياً بنجاح لأغراض الواجهة والتجربة.');
        setCheckoutConfirmation(createSuccessSummary(productKey, refreshedUser));
        getPricingCatalog().then(setCatalog).catch(console.error);
        return;
      }
      window.location.assign(result.checkout_url);
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not start checkout. Please try again.');
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (checkoutState !== 'success' || !sessionId || !user) return;
    if (location.pathname !== '/pricing') return;

    let ignore = false;
    const run = async () => {
      try {
        setBusy('confirm');
        const result = await confirmCheckoutSession(sessionId);
        if (ignore) return;
        if (result?.user) {
          const productKey = typeof window !== 'undefined' ? window.sessionStorage.getItem(CHECKOUT_PRODUCT_KEY) || '' : '';
          const refreshedUser = await refreshUser();
          await getPricingCatalog().then((data) => {
            if (!ignore) setCatalog(data);
          });
          setLocalMessage('تم تحديث الباقة بعد تأكيد الدفع بنجاح.');
          setCheckoutConfirmation(createSuccessSummary(productKey, refreshedUser || result.user));
          window.sessionStorage.removeItem(CHECKOUT_RETURN_TO_KEY);
          window.sessionStorage.removeItem(CHECKOUT_PRODUCT_KEY);
          clearCheckoutQuery();
        }
      } catch (err) {
        if (!ignore) {
          setLocalMessage(err?.response?.data?.error || 'تم الدفع لكن لم يتم تأكيد الباقة بعد. تأكد من webhook أو أعد تحميل الصفحة.');
        }
      } finally {
        if (!ignore) {
          setBusy('');
        }
      }
    };

    run();
    return () => { ignore = true; };
  }, [checkoutState, sessionId, user, refreshUser, returnTo, navigate, location.pathname]);

  return (
    <div className="pricing-page" dir="rtl">
      <style>{styles}</style>
      <section className="wrap">
        <div className="topbar">
          <a className="home-link" href="/" onClick={(event) => { event.preventDefault(); navigate('/'); }}>
            <div style={{ width: 34, height: 34, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff', fontSize: 13, fontWeight: 1000 }}>ITC</div>
            <span>
              <small>الرئيسية</small>
              منصة ITC
            </span>
          </a>
          <button className="back-btn" type="button" onClick={() => navigate(backTarget)}>
            ← رجوع
          </button>
        </div>
        {promoPlan?.active && promoTimeParts && (
          <div className="sticky-promo-bar">
            <div>
              <strong>🔥 Promo Trial ends soon</strong>
              <span>{`90% OFF · ${promoPlan?.price || '5'} SAR only · includes ${promoPlan?.credits ?? 0} AI credits`}</span>
            </div>
            <div className="sticky-timer">
              <div className="sticky-timer-box"><strong>{promoTimeParts.days}</strong><span>Days</span></div>
              <div className="sticky-timer-box"><strong>{promoTimeParts.hours}</strong><span>Hours</span></div>
              <div className="sticky-timer-box"><strong>{promoTimeParts.minutes}</strong><span>Minutes</span></div>
              <div className="sticky-timer-box"><strong>{promoTimeParts.seconds}</strong><span>Seconds</span></div>
            </div>
          </div>
        )}
        <div className="hero">
          <div className="pill-row">
            <div className="pill">🔥 عرض تجريبي لفترة محدودة</div>
            <div className="pill secondary">دفعة واحدة فقط — بدون اشتراك شهري</div>
          </div>
          <h1>اختر الخطة المناسبة لك وادفع مرة واحدة فقط</h1>
          <p>ابدأ بخطة تجريبية منخفضة جداً، أو اختر التدريب الأساسي، أو فعّل التدريب بالذكاء الاصطناعي مع رصيد AI جاهز للاستخدام من أول يوم.</p>
          <div className="countdown">
            ⏳ {promoPlan?.active ? `العرض التجريبي ${promoPlan?.price || '5'} ريال فقط${promoCountdown ? ` · ينتهي خلال ${promoCountdown}` : ' لفترة محدودة'}` : 'العرض التجريبي غير متاح حالياً'}
          </div>
          {promoPlan?.active && promoTimeParts && (
            <div className="hero-timer">
              <div className="hero-timer-label">Promo Trial Countdown</div>
              <div className="hero-timer-value">
                <div className="hero-time-box"><strong>{promoTimeParts.days}</strong><span>Days</span></div>
                <div className="hero-time-box"><strong>{promoTimeParts.hours}</strong><span>Hours</span></div>
                <div className="hero-time-box"><strong>{promoTimeParts.minutes}</strong><span>Minutes</span></div>
                <div className="hero-time-box"><strong>{promoTimeParts.seconds}</strong><span>Seconds</span></div>
              </div>
              <div className="hero-timer-note">🔥 90% OFF for one month only — users can try everything once, then must upgrade.</div>
            </div>
          )}
        </div>

        {checkoutConfirmation && (
          <div className="checkout-success-overlay">
            <div className="checkout-success" role="dialog" aria-modal="true" aria-label="تأكيد الدفع">
              <button className="checkout-success-close" type="button" onClick={closeCheckoutModal} aria-label="إغلاق">
                ×
              </button>
              <div className="checkout-success-top">
                <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div className="checkout-success-badge">✓</div>
                  <div>
                    <div className="checkout-success-kicker">تم تأكيد الدفع</div>
                    <h2>{checkoutConfirmation.title}</h2>
                    <p>{checkoutConfirmation.body}</p>
                  </div>
                </div>
              </div>
              <div className="checkout-success-grid">
                <div className="checkout-success-stat">
                  <strong>{checkoutConfirmation.productLabel}</strong>
                  <span>العنصر الذي تم شراؤه</span>
                </div>
                <div className="checkout-success-stat">
                  <strong>{checkoutConfirmation.planLabel}</strong>
                  <span>الخطة الحالية بعد التحديث</span>
                </div>
                <div className="checkout-success-stat">
                  <strong>{checkoutConfirmation.credits}</strong>
                  <span>إجمالي رصيد AI المتاح الآن</span>
                </div>
              </div>
              <div className="checkout-success-actions">
                <button className="checkout-success-primary" type="button" onClick={closeCheckoutModal}>
                  إنهاء والعودة للرئيسية
                </button>
                <button className="checkout-success-secondary" type="button" onClick={() => navigate(returnTo || '/platform')}>
                  {continueLabel}
                </button>
              </div>
            </div>
          </div>
        )}

        {user && (
          <div style={{ marginBottom: 18, background: '#fff', borderRadius: 22, padding: '16px 18px', boxShadow: '0 16px 40px rgba(15,23,42,.12)', border: '1px solid #e2e8f0', display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>Current plan</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginTop: 4 }}>{user.plan === 'ai' ? 'AI Practice' : user.plan === 'basic' ? 'Basic Practice' : user.plan === 'promo' ? 'Promo Trial' : 'Free'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>AI credits</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginTop: 4 }}>{user.ai_credits ?? 0}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(user.capabilities || {}).filter(([, value]) => value).map(([key]) => (
                <span key={key} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 999, padding: '7px 10px', fontSize: 12, fontWeight: 800 }}>
                  {key.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="busy-note">
            <span className="spinner" />
            <span>{busy === 'confirm' ? 'جاري تأكيد الدفع وتحديث الباقة...' : 'جاري تجهيز عملية التفعيل، يرجى الانتظار...'}</span>
          </div>
        )}

        {(locked || checkoutState || localMessage) && (
          <div style={{ marginBottom: 18, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#e2e8f0', borderRadius: 18, padding: '14px 16px', textAlign: 'center', fontSize: 14, fontWeight: 700 }}>
            {locked && 'هذه الميزة متاحة ضمن خطة مدفوعة. اختر الخطة المناسبة للمتابعة.'}
            {checkoutState === 'success' && 'تمت إعادة توجيهك بعد الدفع. سنفعّل الخطة تلقائياً بعد تأكيد Stripe.'}
            {checkoutState === 'cancelled' && 'تم إلغاء عملية الدفع. يمكنك المحاولة مرة أخرى في أي وقت.'}
            {localMessage}
          </div>
        )}

        <div className={`pricing-grid ${visiblePlans.length < 3 ? 'compact' : ''}`}>
          {visiblePlans.some((plan) => plan.key === 'promo') && (
          <article className="card promo gray">
            <div className="promo-ribbon">LIMITED OFFER</div>
            <div className="discount-tag"><strong>90%</strong><span>خصم</span></div>
            <div className="icon">🚀</div>
            <h2 className="plan">{localize(promoPlan?.content?.title, 'الخطة التجريبية')}</h2>
            <div className="sub">{localize(promoPlan?.content?.subtitle, 'Promo Trial')}</div>
            <div className="hook">{localize(promoPlan?.content?.hook, 'أفضل طريقة لتجربة المنصة كاملة بسعر رمزي قبل الترقية لاحقاً إلى AI Practice.')}</div>
            <div className="promo-meta">
              <span className="promo-chip">جرّب كل شيء مرة واحدة</span>
              <span className="promo-chip">{promoPlan?.credits ?? 0} AI credits فقط</span>
              <span className="promo-chip">الهدف: الترقية لاحقاً</span>
            </div>
            <div className="promo-timer">⏰ {promoCountdown || (promoPlan?.active ? 'لفترة محدودة' : 'العرض غير متاح')}</div>

            <div className="price-box">
              <div className="old-price"><span>كان</span><span className="num">50</span><span>SAR</span></div>
              <div style={{ textAlign: 'center' }}><span className="save-line">{`لفترة محدودة — ادفع ${promoPlan?.price || '5'} ريال فقط`}</span></div>
              <div className="new-price">
                <span className="term">/مرة واحدة</span>
                <span className="num">{promoPlan?.price || '5'}</span>
                <span className="currency">SAR</span>
              </div>
            </div>

            <ul>
              {featuresFor(promoPlan, [
                'دخول كامل للمنصة',
                'القراءة + الكتابة + المحادثة + الاختبار الكامل',
                `يشمل ${promoPlan?.credits ?? 0} AI credits للتجربة`,
                'بعد انتهاء الرصيد يلزم الترقية إلى AI Practice',
              ]).map((feature) => <li key={feature}><span className="tick">✓</span><span>{feature}</span></li>)}
            </ul>

            <button className={`cta ${busy === 'promo' ? 'loading' : ''}`} type="button" onClick={() => handleCheckout('promo')} disabled={isProcessing || currentPlan !== 'free' || !promoPlan?.active} style={{ opacity: currentPlan !== 'free' || isProcessing || !promoPlan?.active ? .65 : 1, cursor: currentPlan !== 'free' || isProcessing || !promoPlan?.active ? 'not-allowed' : 'pointer' }}>
              {busy === 'promo' && <span className="spinner" />}
              {busy === 'promo' ? 'جاري التحويل إلى الدفع...' : !promoPlan?.active ? 'العرض غير متاح الآن' : currentPlan === 'free' ? localize(promoPlan?.content?.cta, 'ابدأ العرض التجريبي') : 'متاح للمستخدم الجديد فقط'}
            </button>
          </article>
          )}

          {visiblePlans.some((plan) => plan.key === 'basic') && (
          <article className="card blue">
            <div className="discount-tag"><strong>50</strong><span>SAR</span></div>
            <div className="icon">✏️</div>
            <h2 className="plan">{localize(basicPlan?.content?.title, 'تدريب أساسي')}</h2>
            <div className="sub">{localize(basicPlan?.content?.subtitle, 'Basic Practice')}</div>
            <div className="hook">{localize(basicPlan?.content?.hook, 'خيار مناسب للطالب الذي يريد تدريب ثابت على القراءة والكتابة بدون AI marking أو محادثة.')}</div>

            <div className="price-box">
              <div style={{ textAlign: 'center' }}><span className="save-line">دفعة واحدة — بدون تجديد تلقائي</span></div>
              <div className="new-price">
                <span className="term">/مرة واحدة</span>
                <span className="num">{basicPlan?.price || '50'}</span>
                <span className="currency">SAR</span>
              </div>
            </div>

            <ul>
              {featuresFor(basicPlan, [
                'الوصول إلى المنصة واختبارات القراءة المصححة تلقائياً',
                'واجهة الكتابة + نموذج إجابة للمقارنة',
                'بدون AI marking',
                'بدون Speaking test وبدون شراء credits إضافية',
              ]).map((feature) => <li key={feature}><span className="tick">✓</span><span>{feature}</span></li>)}
            </ul>

            <button className={`cta ${busy === 'basic' ? 'loading' : ''}`} type="button" onClick={() => handleCheckout('basic')} disabled={isProcessing || currentPlan === 'basic' || currentPlan === 'ai' || currentPlan === 'promo'} style={{ opacity: currentPlan === 'basic' || currentPlan === 'ai' || currentPlan === 'promo' || isProcessing ? .65 : 1, cursor: currentPlan === 'basic' || currentPlan === 'ai' || currentPlan === 'promo' || isProcessing ? 'not-allowed' : 'pointer' }}>
              {busy === 'basic' && <span className="spinner" />}
              {busy === 'basic' ? 'جاري التحويل إلى الدفع...' : currentPlan === 'basic' ? 'خطتك الحالية' : currentPlan === 'promo' ? 'الترقية الموصى بها هي AI Practice' : currentPlan === 'ai' ? 'تم تفعيل الخطة الأعلى' : localize(basicPlan?.content?.cta, 'اختر التدريب الأساسي')}
            </button>
          </article>
          )}

          {visiblePlans.some((plan) => plan.key === 'ai') && (
          <article className="card green popular">
            <div className="top-badge">{localize(aiPlan?.content?.badge, 'الأكثر قيمة')} ⭐</div>
            <div className="discount-tag"><strong>{aiPlan?.credits ?? 0}</strong><span>credits</span></div>
            <div className="icon">🤖</div>
            <h2 className="plan">{localize(aiPlan?.content?.title, 'تدريب بالذكاء الاصطناعي')}</h2>
            <div className="sub">{localize(aiPlan?.content?.subtitle, 'AI Practice')}</div>
            <div className="hook">{localize(aiPlan?.content?.hook, 'الخطة الكاملة: كل شيء في Basic + AI marking + Speaking + إمكانية شراء credits إضافية.')}</div>

            <div className="price-box">
              <div style={{ textAlign: 'center' }}><span className="save-line">{`يشمل ${aiPlan?.credits ?? 0} AI credits من البداية`}</span></div>
              <div className="new-price">
                <span className="term">/مرة واحدة</span>
                <span className="num">{aiPlan?.price || '150'}</span>
                <span className="currency">SAR</span>
              </div>
            </div>

            <ul>
              {featuresFor(aiPlan, [
                'كل مزايا Basic Practice',
                'AI marking للكتابة وفق المعايير',
                'Speaking test + AI examiner + feedback',
                'يمكن شراء credits إضافية: 5 / 10 / 25',
              ]).map((feature) => <li key={feature}><span className="tick">✓</span><span>{feature}</span></li>)}
            </ul>

            <button className={`cta ${busy === 'ai' ? 'loading' : ''}`} type="button" onClick={() => handleCheckout('ai')} disabled={isProcessing || currentPlan === 'ai'} style={{ opacity: currentPlan === 'ai' || isProcessing ? .65 : 1, cursor: currentPlan === 'ai' || isProcessing ? 'not-allowed' : 'pointer' }}>
              {busy === 'ai' && <span className="spinner" />}
              {busy === 'ai' ? 'جاري التحويل إلى الدفع...' : currentPlan === 'basic' ? 'رقِّ إلى AI Practice' : currentPlan === 'ai' ? 'خطتك الحالية' : localize(aiPlan?.content?.cta, 'فعّل AI Practice')}
            </button>
          </article>
          )}
        </div>

        <div className="guarantee">
          <span>جميع الأسعار بالريال السعودي وتشمل ضريبة القيمة المضافة</span>
          <span>كل الخطط دفعة واحدة فقط — بدون اشتراكات</span>
          <span>شراء credits الإضافية متاح فقط لمستخدمي AI Practice</span>
        </div>

        {user?.plan === 'ai' && (catalog?.credit_packs_enabled ?? (catalog?.credit_packs || []).length > 0) && (
          <div style={{ marginTop: 22, background: '#fff', borderRadius: 24, padding: '22px 20px', boxShadow: '0 20px 40px rgba(15,23,42,.12)', border: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>شراء رصيد إضافي</h3>
              <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 14 }}>جرّب الرصيد الإضافي الآن محلياً لرؤية تحديث الرصيد وتمكين الميزات مباشرة.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
              {(catalog?.credit_packs || []).map((pack) => (
                <div key={pack.key} style={{ border: '1px solid #e2e8f0', borderRadius: 20, padding: 16, background: '#f8fafc' }}>
                  <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 900 }}>{localize(pack.content?.title, pack.name)}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{localize(pack.content?.hook, pack.description)}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 12 }}>
                    <span style={{ fontSize: 30, fontWeight: 1000, color: '#059669' }}>{pack.price}</span>
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>SAR</span>
                  </div>
                  <button className={`cta ${busy === pack.key ? 'loading' : ''}`} type="button" style={{ marginTop: 14, background: 'linear-gradient(135deg,#0ea5e9,#2563eb)' }} disabled={isProcessing} onClick={() => handleCheckout(pack.key)}>
                    {busy === pack.key && <span className="spinner" />}
                    {busy === pack.key ? 'جاري التحويل إلى الدفع...' : localize(pack.content?.cta, `شراء ${pack.credits} credits`)}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
